import { Agent, CheckoutSession, Money, PaymentIntent, PaymentMethodType } from '@nemuru-eng/js-api-client';

import { AbstractSinglePaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractSinglePaymentMethod';
import { PaymentMethodFactory } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/PaymentMethodFactory';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { AbstractProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/AbstractProgress';
import { FactoryProgress } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/Progress/FactoryProgress';
import { Action } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/types/PendingAction';
import { ActionsMap } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/SinglePayment/NextActions/maps/actions.map';

export class SinglePaymentMethodDetailEntity extends PaymentMethodDetailEntity {
  static allowed = [PaymentMethodType.CARD_PAYMENT, PaymentMethodType.A2A_PAYMENT];

  public paymentMethod: AbstractSinglePaymentMethod | undefined = undefined;
  protected _progress?: AbstractProgress;

  constructor(
    paymentMethodType: PaymentMethodType,
    checkoutSession: CheckoutSession,
    agent: Agent,
    paymentIntent?: PaymentIntent,
  ) {
    super(paymentMethodType, checkoutSession, agent, paymentIntent);
  }

  public static isAllowed(type: PaymentMethodType): boolean {
    return this.allowed.includes(type);
  }

  public async refresh(): Promise<this> {
    try {
      this.setPaymentMethodFromCalculator();
    } catch (error) {
      this.setError(error as Error);
    }

    if (!this.paymentMethodId) {
      this._isRefreshed = true;
      return this;
    }

    try {
      await this.refreshDataFromCardPayment();
    } catch (error) {
      this.setError(error as Error);
      console.log(error);
    }

    this._isRefreshed = true;
    return this;
  }

  private async refreshDataFromCardPayment(): Promise<this> {
    this.actions = this.getPendingActions() ?? undefined;
    this._progress = await this.getProgress();
    return this;
  }

  private setPaymentMethodFromCalculator(): void {
    if (this.paymentMethod) return;

    const paymentMethodEntity = PaymentMethodFactory.byType(this.type);

    if (!(paymentMethodEntity instanceof AbstractSinglePaymentMethod)) {
      throw new Error('Payment method entity is not a single payment method');
    }

    paymentMethodEntity.setup(this.totalAmount.value, this.totalAmount.currency);

    this.paymentMethod = paymentMethodEntity;
  }

  private getPendingActions(): Action[] | null {
    return ActionsMap.fromPaymentMethodDetail(this);
  }

  public async isActionCompleted(): Promise<boolean> {
    if (!this.paymentMethodId) {
      return false;
    }

    if (this.isFinalStatus()) {
      return true;
    }

    return false;
  }

  public clone(): this {
    throw new Error('Method not implemented.');
  }

  public get totalAmount(): Money {
    return {
      value: this.checkoutSession.totalPrice.amount - (this.checkoutSession.downPayment.amount.amount ?? 0),
      currency: this.checkoutSession.totalPrice.currency,
    };
  }

  protected async getProgress(): Promise<AbstractProgress> {
    if (!this.paymentMethodId) {
      throw new Error('PaymentMethodId is not set to get progress');
    }

    const progress = FactoryProgress.create(this.paymentMethodId, this.type);
    return await progress.calculateProgress();
  }

  public get progress(): AbstractProgress | undefined {
    return this._progress;
  }
}
